import React, { PureComponent } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

import RainbowInput from '@ott/rainbow-input';
import Overlay from '@ott/overlay';
import Modal from '@ott/modal';

import cls from 'classnames/bind';
import $get from 'lodash/get';
import $isEqual from 'lodash/isEqual';
import $isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import { withLayoutContext } from '@ott/layout-context';

import _noop from 'lodash/noop';
import Options from './blocks/Options/Options';
import MobileHeader from './blocks/MobileHeader/MobileHeader';

import s from './GeoAutocomplete.scss';

import {
  OFFSET_FROM_TRIGGER,
  OFFSET_FROM_SCREEN_BOTTOM_BORDER,
  OFFSET_FROM_SIDE_BORDER,
  FULL_SCREEN_BREAKPOINT,
} from './constants';

const compName = 'GeoAutocomplete';
const locators = {
  compName,
  from: `${compName}--from`,
  to: `${compName}--to`,
};

const ua = typeof window !== 'undefined' && window.navigator.userAgent;
const isIE = ua && (ua.includes('MSIE') || ua.includes('Trident/'));

const cx = cls.bind(s);

class GeoAutocomplete extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    specificLocators: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),

    isLoading: PropTypes.bool,

    value: PropTypes.any,
    valueKey: PropTypes.string,
    items: PropTypes.array,

    /*  func (value, props, state) */
    onText: PropTypes.func,
    /*  func (item, options ({skipFocusNextControl })) */
    onOptionSelect: PropTypes.func,

    onFocus: PropTypes.func,

    initialActiveItem: PropTypes.object,

    placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']), // from Position

    showExtraData: PropTypes.bool,
    showOneLine: PropTypes.bool,

    /* start for RainbowInput Props */
    name: PropTypes.string,
    type: PropTypes.string,
    placeholder: PropTypes.string,
    focusedStatePlaceholder: PropTypes.string,
    initialValue: PropTypes.any,
    valid: PropTypes.bool,
    active: PropTypes.bool,
    dirty: PropTypes.bool,
    touched: PropTypes.bool,
    error: PropTypes.any,
    usePortalOverlay: PropTypes.bool,
    childrenClassNames: PropTypes.shape({
      input: PropTypes.string,
      placeholderOverlay: PropTypes.string,
      overlayAdditionalInfo: PropTypes.string,
      overlay: PropTypes.string,
      optionsContainer: PropTypes.string,
    }),
    /* end RainbowInput Props */
    disableFocusOnSelect: PropTypes.bool,
    isTouchDevice: PropTypes.bool.isRequired,
    mobileModalClassName: PropTypes.string,
    skipPostLoad: PropTypes.bool,
  };

  static defaultProps = {
    skipPostLoad: false,
    valueKey: null,
    childrenClassNames: {},
    isLoading: false,
    specificLocators: null,
    placement: 'left',
    usePortalOverlay: false,
    showOneLine: false,
    disableFocusOnSelect: false,
    onFocus: _noop,
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.handleClick = this.handleClick.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.handleEnter = this.handleEnter.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleSelect = this.handleSelect.bind(this);

    // Увеличиваем дебаунс у ие из-за проглатывания букв в геоинпуте
    // при одновременном вводe и получении саджеста
    // Кейс вопроизводился как минимум на Windows 10, IE 11.540
    this.handleText = debounce(this.handleText.bind(this), isIE ? 1000 : 100, {
      leading: true,
    });

    this.setControlRef = this.setRef.bind(this, 'controlRef');
    this.containerRef = React.createRef();

    this.state = {
      isOpen: false,
      isMobile: false,
      value: props.value || props.initialValue,
      initialItem: {},
      activeItem: props.initialActiveItem,
    };
  }

  componentDidMount() {
    const { value = '' } = this.state;

    const { onText } = this.props;

    if (value.trim().length > 0) {
      // здесь вызываем без debounce потому что отрендерится может сразу несолько на странице
      onText(value, this.props, this.state);
    }

    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  }

  componentWillReceiveProps(nextProps) {
    const { isOpen, isFocused, value, isMobile } = this.state;
    const { initialActiveItem, valueKey, items = [], skipPostLoad } = nextProps;
    const isLoaded = this.props.isLoading && !nextProps.isLoading;
    const isInitialUpdate = !$isEqual(this.props.initialActiveItem, initialActiveItem);
    const isSuggestEmty = !items.length;
    const isSuggestPreload = !value && !isSuggestEmty;
    const open = (isSuggestPreload && !isMobile && isFocused) || isOpen;
    const activeItem = this.findActiveItem(nextProps);
    const isSuggestPostLoad =
      isLoaded && !isFocused && !isOpen && value && !$isEqual(initialActiveItem, activeItem);
    const shouldUpdate = typeof nextProps.value !== 'undefined';

    if (isInitialUpdate) {
      this.setState({
        activeItem: initialActiveItem,
        value: $get(initialActiveItem, valueKey),
        isOpen: open,
      });
    } else if (isSuggestPostLoad && !isSuggestEmty && !skipPostLoad) {
      this.handleSelect(activeItem, { skipFocusNextControl: true });
    } else if (isSuggestPostLoad && isSuggestEmty && shouldUpdate) {
      this.handleSelect(
        {
          [valueKey]: value,
        },
        { skipFocusNextControl: true }
      );
    } else if (shouldUpdate) {
      this.setState({
        activeItem,
        isOpen: open,
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  setRef = (key, element) => {
    this[key] = element;
  };

  capitalizeText = (value = '', valueKey, activeItem) => {
    // если набрали "мос" делаем "Мос"
    const text = $get(activeItem, valueKey);
    if (text) {
      if (text.toLowerCase().startsWith(value.toLowerCase())) {
        return text.substring(0, value.length);
      }
    }

    return value;
  };

  isGroupFirst = items => {
    return Boolean(items && items[0] && items[0].isGroup);
  };

  isItemsVisible = () => {
    const { items } = this.props;
    return items && items.length && !items.every(item => item.isGroup);
  };

  findActiveItem = (props = this.props) => {
    const { items = [], initialActiveItem = {} } = props;

    if (items.length === 0) {
      return initialActiveItem;
    }

    const idx = this.isGroupFirst(items) ? 1 : 0;
    return items[idx];
  };

  _updateIndex(direction) {
    const { items = [] } = this.props;

    if (!this.isItemsVisible()) {
      return;
    }

    const { activeItem } = this.state;

    this._activeOptionReveal = true;

    const currentIndex = items.indexOf(activeItem);

    let nextIdx;
    if (direction === 'down') {
      nextIdx = this.updateIndexDown(currentIndex, items);
    } else {
      nextIdx = this.updateIndexUp(currentIndex, items);
    }

    const nextItem = items[nextIdx];

    if (nextItem) {
      this.setState({
        activeItem: nextItem,
      });
    }
  }

  updateIndexDown(cur, items) {
    const last = items.length - 1;

    if (cur < 0 || cur >= last) {
      return this.isGroupFirst(items) ? 1 : 0;
    }

    const nextItem = items[cur + 1];
    if (nextItem && nextItem.isGroup) {
      return this.updateIndexDown(cur + 1, items);
    }

    if (cur < last) {
      return cur + 1;
    }

    return last;
  }

  updateIndexUp(cur, items) {
    const last = items.length - 1;

    if (cur <= 0 || cur > last) {
      return last;
    }

    const prevItem = items[cur - 1];
    if (prevItem && prevItem.isGroup) {
      return this.updateIndexUp(cur - 1, items);
    }

    return cur - 1;
  }

  getOverlayPosition = () => {
    const { usePortalOverlay } = this.props;
    if (!usePortalOverlay) {
      return {};
    }

    if (!this.controlRef || !this.controlRef.inputRef) {
      return {};
    }

    const inputPosition = this.controlRef.inputRef.getBoundingClientRect();

    return {
      top: (window.scrollY || window.pageYOffset) + inputPosition.bottom + OFFSET_FROM_TRIGGER,
      left: inputPosition.left - OFFSET_FROM_SIDE_BORDER,
      right: window.innerWidth - inputPosition.right - OFFSET_FROM_SIDE_BORDER,
      maxHeight: window.innerHeight - inputPosition.bottom - OFFSET_FROM_SCREEN_BOTTOM_BORDER,
    };
  };

  openSelect() {
    const { isOpen, isMobile, value } = this.state;
    const { initialActiveItem } = this.props;

    if (isOpen) {
      return;
    }

    this.setState({
      isOpen: true,
      value: isMobile ? '' : value,
      initialItem: initialActiveItem,
    });
  }

  hideSelect = () => {
    this.setState({
      isOpen: false,
      initialItem: {},
    });
  };

  focusOnInput() {
    const inputRef = this.controlRef && this.controlRef.inputRef;
    if (inputRef && inputRef.focus) {
      inputRef.focus();
    }
  }

  handleToggle() {
    const { isOpen } = this.state;
    if (!isOpen) {
      this.openSelect();
    } else {
      this.hideSelect();
    }
  }

  handleResize = () => {
    if (typeof window !== 'undefined') {
      const isMobile = window.innerWidth < FULL_SCREEN_BREAKPOINT;
      this.setState({ isMobile });
    } else {
      this.setState({ isMobile: false });
    }

    const { isOpen } = this.state;
    if (isOpen) {
      this.setState({ position: this.getOverlayPosition() });
    }
  };

  handleClose = () => {
    const { initialItem } = this.state;

    this.handleSelect(initialItem, { skipFocusNextControl: true });
  };

  handleCloseButtonClick = e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
        e.nativeEvent.stopImmediatePropagation();
      }
    }

    this.handleClose();
  };

  // debounced
  handleText(value, clearField = true) {
    this.setState({
      activeItem: null,
    });

    const { onText } = this.props;
    onText(value, this.props, this.state, clearField);
  }

  handleChange(value, clearField) {
    this.setState(
      {
        isOpen: true,
        position: this.getOverlayPosition(),
        value,
      },
      () => {
        this.handleText(value, clearField);
      }
    );
  }

  handleSelect(item, options) {
    const { valueKey, onOptionSelect, disableFocusOnSelect } = this.props;
    const { isMobile } = this.state;
    const defaultOptions = {
      skipFocusNextControl: !!isMobile,
    };
    const value = $get(item, valueKey) || '';

    this.setState(
      {
        activeItem: item,
        value,
        isOpen: false,
        initialItem: {},
      },
      () => {
        // в сафари мобайл происходит скролл к выбранной опции после закрытия Options
        // частично потому что мы тыкаем по div/span. а не по input/button
        // перебиваем поведения скрола к input по которому открывали Options
        if (isMobile && (options || defaultOptions).skipFocusNextControl && !disableFocusOnSelect) {
          this.focusOnInput();
        }
        // прокинет activeItem сверху
        return onOptionSelect(item, options || defaultOptions, this.props, this.state, this);
      }
    );
  }

  handleOptionSelect = (item, options) => {
    const { initialItem, value, isMobile } = this.state;

    const hasInitialItem = initialItem && initialItem.name && initialItem.id;
    const hasValue = !hasInitialItem && !value;

    let newOptions = options;

    if (hasValue) {
      if (!newOptions) {
        newOptions = { skipFocusNextControl: !!isMobile };
      }
      newOptions.popularItem = true;
    }

    this.handleSelect(item, newOptions);
  };

  handleFocus() {
    const { isMobile, isOpen, value = '' } = this.state;
    const { initialActiveItem, items = [], onText, onFocus } = this.props;
    const isSuggestPreload = !value && items.length;
    const open = (isSuggestPreload && !isMobile) || isOpen;

    this.setState({
      isFocused: !isMobile,
      initialItem: initialActiveItem,
      isOpen: open,
      isOpenByHandleFocus: open,
      position: this.getOverlayPosition(),
    });

    if (!value && (!isMobile || !initialActiveItem || !initialActiveItem.valueKey)) {
      onText(value, this.props, this.state, false);
    }

    const { controlRef } = this;

    if (!isMobile && controlRef && controlRef.select) {
      controlRef.select();
    }

    onFocus(value, this.props, this.state);
  }

  handleBlur() {
    const { isMobile, isOpen, value = '' } = this.state;
    const { initialActiveItem } = this.props;

    const newState = {
      isFocused: false,
      isOpen: isMobile ? isOpen : false,
    };

    // если ничего не ввели и убрали фокус с инпута, то убираем предзаполнение
    if (!value && (!isMobile || !initialActiveItem || !initialActiveItem.valueKey)) {
      newState.activeItem = null;
    }

    this.setState({
      ...newState,
    });
  }

  handleEnter(e) {
    const { activeItem, isOpen, isMobile, value = '' } = this.state;
    const { valueKey, isLoading, items } = this.props;

    if (value !== $get(activeItem, valueKey) || value === '' || isOpen || activeItem) {
      e && e.preventDefault();
    }

    if ((isMobile && isMobile) || activeItem) {
      e && e.stopPropagation();
    }

    if (isOpen && isMobile) {
      if (isLoading) {
        return;
      }

      if (this.isItemsVisible()) {
        const index = this.isGroupFirst(items) ? 1 : 0;
        this.handleSelect(items[index]);
      } else {
        this.handleSelect({ [valueKey]: value });
      }

      return;
    }

    if (!$isEmpty(activeItem)) {
      this.handleSelect(activeItem);
    } else {
      this.handleSelect({
        [valueKey]: value,
      });
    }
  }

  handleTab() {
    const { activeItem, value } = this.state;
    const { valueKey } = this.props;

    if (!$isEmpty(activeItem)) {
      this.handleSelect(activeItem, { skipFocusNextControl: true });
    } else {
      this.handleSelect(
        {
          [valueKey]: value,
        },
        { skipFocusNextControl: true }
      );
    }
  }

  handleActiveOptionSelect = item => {
    const { isOpen } = this.state;

    if (isOpen) {
      // чтобы не отрабатывало, когда выбрали option, но dropdown не успел закрыться
      this.setState({
        activeItem: item,
      });
    }
  };

  handleClick() {
    const { isOpenByHandleFocus, isMobile } = this.state;
    if (isMobile) {
      this.openSelect();
      return;
    }

    if (isOpenByHandleFocus) {
      return;
    }

    this.handleToggle();
  }

  handleOptionFocus = e => {
    const { isMobile } = this.state;

    // иначе safari в mobile вызовет focus на другом элементе и будет прыгать скролл
    if (!isMobile) {
      e && e.preventDefault();
      e && e.stopPropagation();
    }
  };

  handleOptionBlur = () => {};

  handleKeyDown(e) {
    const { isOpen } = this.state;

    switch (e.key) {
      case 'ArrowDown':
        if (e.shiftKey || e.metaKey) {
          return;
        }
        e.preventDefault();
        if (isOpen) {
          this._updateIndex('down');
        } else {
          this.handleEnter(e);
        }
        break;
      case 'ArrowUp':
        if (e.shiftKey || e.metaKey) {
          return;
        }
        e.preventDefault();
        if (isOpen) {
          this._updateIndex('up');
        } else {
          this.handleEnter(e);
        }
        break;
      case 'Enter':
        this.handleEnter(e);
        break;
      case 'Tab':
        this.handleTab(e);
        break;
      case 'Escape':
        this.handleClose();
        break;
      default:
      // do nothing
    }
  }

  renderInput() {
    const { childrenClassNames, valueKey, isLoading, placeholder } = this.props;

    const { isOpen, isMobile, activeItem, initialItem, value: stateValue } = this.state;

    let value;

    if (isMobile && isOpen) {
      value = $get(initialItem, valueKey) || '';
    } else {
      value = stateValue || '';
    }

    const inputProps = {
      ...this.props, // very bad
      spellCheck: false,
      onFocus: this.handleFocus,
      onClick: this.handleClick,
      onBlur: this.handleBlur,
      onKeyDown: this.handleKeyDown,
      isOpen,
      onChange: this.handleChange,
      placeholder,
      additionalInfo: this.renderAdditionalInfo(value, valueKey, activeItem),
      overlayAdditionalInfo: this.renderOverlayAdditionalInfo(activeItem, isLoading),
      'data-error-placement': 'top',
      'data-locator': 'GeoAutocomplete-input',
      value,
    };

    if (!isMobile) {
      inputProps.value = this.capitalizeText(inputProps.value, valueKey, activeItem);
    }

    return (
      <RainbowInput
        {...inputProps}
        className={cx('input', childrenClassNames.input)}
        childrenClassNames={{
          input: cx('inputField'),
        }}
        ref={this.setControlRef}
        additionalInfoOnly={true}
        readOnly={isMobile}
      />
    );
  }

  renderOptions() {
    const { isMobile, activeItem, value } = this.state;
    const {
      title,
      childrenClassNames,
      items,
      valueKey,
      isLoading,
      showExtraData,
      showOneLine,
      usePortalOverlay,
      isTouchDevice,
    } = this.props;

    const mobileValue = this.capitalizeText(value, valueKey, activeItem);

    return (
      <Options
        title={title}
        items={items}
        value={mobileValue}
        valueKey={valueKey}
        activeItem={activeItem}
        childrenClassNames={childrenClassNames}
        isMobile={isMobile}
        isLoading={isLoading}
        showExtraData={showExtraData}
        showOneLine={showOneLine}
        usePortalOverlay={usePortalOverlay}
        isTouchDevice={isTouchDevice}
        handleClose={isMobile ? this.handleClose : this.hideSelect}
        handleOptionSelect={this.handleOptionSelect}
        handleActiveOptionSelect={this.handleActiveOptionSelect}
        handleOptionFocus={this.handleOptionFocus}
        handleOptionBlur={this.handleOptionBlur}
      >
        {isMobile && (
          <MobileHeader
            title={title}
            value={mobileValue}
            childrenClassNames={childrenClassNames}
            handleKeyDown={this.handleKeyDown}
            handleChange={this.handleChange}
            handleCloseButtonClick={this.handleCloseButtonClick}
            throbberNode={this.renderThrobber('throbber--header', isLoading)}
            additionalInfo={this.renderAdditionalInfo(mobileValue, valueKey, activeItem)}
          />
        )}
      </Options>
    );
  }

  renderOverlay() {
    const { isOpen, isMobile, position } = this.state;
    const { placement, childrenClassNames, usePortalOverlay, mobileModalClassName } = this.props;

    if (!isOpen) {
      return null;
    }

    // не показываем оверлай, если ничего нет или оставиль только группы
    if (!isMobile && !this.isItemsVisible()) {
      return null;
    }

    const optionsNode = this.renderOptions();

    if (isMobile) {
      return (
        <Modal
          isOpen={true}
          preventUserClose={true}
          fullScreenOnMobile={true}
          fullScreenBreakpoint={FULL_SCREEN_BREAKPOINT}
          contentOptions={{
            withoutPadding: true,
          }}
          layoutContainerClassName={mobileModalClassName}
        >
          {optionsNode}
        </Modal>
      );
    }

    if (usePortalOverlay) {
      if (!this.portalRoot) {
        this.portalRoot = document.createElement('div');
        document.body.appendChild(this.portalRoot);
      }

      return createPortal(
        <div className={cx('overlay', childrenClassNames.overlay)} style={position}>
          {optionsNode}
        </div>,
        this.portalRoot
      );
    }

    return (
      <Overlay
        show={true}
        container={this.containerRef.current}
        isAbsolutePosition={true}
        isFullScreen={isMobile}
        placement={placement}
      >
        {optionsNode}
      </Overlay>
    );
  }

  renderAdditionalInfo = (value = '', valueKey, activeItem) => {
    if (!value) {
      return null;
    }

    let invisiblePart = '';
    let visiblePart = '';

    let text = $get(activeItem, valueKey);

    if (text && activeItem && activeItem.country) {
      text += `, ${activeItem.country}`;
    }

    if (text && activeItem && activeItem.description) {
      text += `, ${activeItem.description}`;
    }

    if (text) {
      if (text.toLowerCase().startsWith(value.toLowerCase())) {
        invisiblePart = text.substring(0, value.length);
        visiblePart = text.substring(value.length);
      }
    }
    if (!visiblePart) {
      return null;
    }

    // значение поля выводится прозрачным текстом поверх инпута,
    // чтобы подвинуть дополнительные данный (название страны)
    return (
      <div className={cx('additionalInfo')}>
        <span className={cx('transparent')}>{invisiblePart}</span>
        <span>{visiblePart}</span>
      </div>
    );
  };

  renderOverlayAdditionalInfo = (activeItem, isLoading) => {
    if (!activeItem || isLoading) {
      return null;
    }

    return <div className={cx('iata')}>{activeItem.code}</div>;
  };

  renderThrobber = (className, isLoading) => {
    return (
      <div
        className={cx('throbber', className, {
          'throbber--isVisible': isLoading,
        })}
      />
    );
  };

  render() {
    const { className, specificLocators, isLoading } = this.props;
    const { isOpen } = this.state;

    return (
      <div
        ref={this.containerRef}
        className={cx(compName, className)}
        data-locator={cls(compName, specificLocators)}
      >
        {this.renderInput()}
        {this.renderOverlay()}
        {isOpen && this.renderThrobber(null, isLoading)}
      </div>
    );
  }
}

export default withLayoutContext(GeoAutocomplete);

/* Используется для старых форм, где нужно экстендить локлаьные автокомплиты */
export const GeoAutocompleteExtendable = GeoAutocomplete;

export { locators, Options, MobileHeader };
